import { Controller } from "@hotwired/stimulus"

// Single, form-agnostic validation controller built on the native Constraint Validation API
// (required, type=email, pattern, min, etc.) instead of a bespoke rules engine.
//
// Simple fields need only the native HTML5 attribute plus:
//   data-action="blur->form-validation#validate"
//
// Fields with cross-field/conditional logic add a `data-form-validation-custom` rule name,
// which runs before checkValidity() and reports via el.setCustomValidity(...):
//   - "unless"            data-form-validation-unless="<selector of a checkbox>"
//                          field is required unless the referenced checkbox is checked
//   - "wordCount"         data-form-validation-min="100" data-form-validation-max="200"
//                          value must contain a word count within [min, max]
//   - "differ"            data-form-validation-differ="<group name>"
//                          data-form-validation-differ-message="<optional message>"
//                          value must not match any other field sharing the same group name
//   - "requireOneChecked" (on every radio in a group) at least one radio must be checked
//
// Forms using this controller must set `novalidate` so the browser's native validation
// bubbles stay disabled — errors are rendered as a custom .error-text span instead.
//
// Error messages are prefixed with the field's label by default (e.g. "Keywords can't
// be blank"), read from the associated <label> text. Override or suppress this per field
// with data-form-validation-label="Short label" (or ="" to omit the prefix entirely).
export default class extends Controller {
  validate(event) {
    const el = event.currentTarget
    this.runCustomRule(el)
    this.report(el)
  }

  validateAll(event) {
    const fields = this.element.querySelectorAll("[data-action*='form-validation#validate']")
    let hasErrors = false
    fields.forEach(el => {
      this.runCustomRule(el)
      if (!this.report(el)) hasErrors = true
    })
    if (hasErrors) event.preventDefault()
  }

  report(el) {
    const valid = el.checkValidity()
    this.renderError(el, valid ? "" : this.messageFor(el))
    return valid
  }

  // Builds our own consistent copy from the validity state, rather than trusting
  // el.validationMessage, which is browser- and locale-dependent. Custom
  // setCustomValidity() messages are used as-is (no label prefix).
  messageFor(el) {
    if (el.validity.customError) return el.validationMessage

    let message
    if (el.validity.valueMissing) message = "can't be blank"
    else if (el.validity.typeMismatch) message = el.type === "email" ? "is not a valid email address" : "is not a valid value"
    else if (el.validity.patternMismatch) message = el.title || "is invalid"
    else if (el.validity.rangeUnderflow) message = `must be greater than or equal to ${el.min}`
    else if (el.validity.rangeOverflow) message = `must be less than or equal to ${el.max}`
    else message = el.validationMessage

    const label = this.labelFor(el)
    return label ? `${label} ${message}` : message
  }

  labelFor(el) {
    if ("formValidationLabel" in el.dataset) return el.dataset.formValidationLabel || null
    const wrapper = el.closest(".form-group") || el.parentElement
    const text = wrapper.querySelector("label")?.textContent
    return text?.replace(/\*$/, "").trim() || null
  }

  renderError(el, message) {
    const wrapper = el.closest(".form-group") || el.parentElement
    let errorEl = wrapper.querySelector(".error-text")

    if (message) {
      if (!errorEl) {
        errorEl = document.createElement("span")
        errorEl.className = "error-text mt-2 text-sm text-red-600"
        errorEl.id = `${el.id || el.name}-error`
        wrapper.appendChild(errorEl)
      }
      errorEl.textContent = message
      el.setAttribute("aria-invalid", "true")
      el.setAttribute("aria-describedby", errorEl.id)
    } else if (errorEl) {
      errorEl.remove()
      el.removeAttribute("aria-invalid")
      el.removeAttribute("aria-describedby")
    }
  }

  runCustomRule(el) {
    switch (el.dataset.formValidationCustom) {
      case "unless":
        this.unless(el)
        break
      case "wordCount":
        this.wordCount(el)
        break
      case "differ":
        this.differ(el)
        break
      case "requireOneChecked":
        this.requireOneChecked(el)
        break
    }
  }

  unless(el) {
    const selector = el.dataset.formValidationUnless
    const toggle = selector && this.element.querySelector(selector)
    if (toggle && toggle.checked) {
      el.setCustomValidity("")
    } else if (!el.value.trim()) {
      el.setCustomValidity("This field is required")
    } else {
      el.setCustomValidity("")
    }
  }

  wordCount(el) {
    const min = parseInt(el.dataset.formValidationMin, 10)
    const max = parseInt(el.dataset.formValidationMax, 10)
    const words = el.value.trim().split(/\s+/).filter(Boolean)

    if (!el.value.trim()) {
      el.setCustomValidity("This field is required")
    } else if (words.length < min || words.length > max) {
      el.setCustomValidity(`Your answer must be between ${min} and ${max} words`)
    } else {
      el.setCustomValidity("")
    }
  }

  differ(el) {
    const group = el.dataset.formValidationDiffer
    const partners = this.element.querySelectorAll(`[data-form-validation-differ="${group}"]`)
    const values = Array.from(partners)
      .map(p => (p.value || "").trim().toLowerCase())
      .filter(Boolean)
    const hasDuplicate = values.length > 1 && new Set(values).size < values.length
    const message = el.dataset.formValidationDifferMessage || "must be different from the other value above"

    el.setCustomValidity(hasDuplicate ? message : "")
  }

  requireOneChecked(el) {
    const group = this.element.querySelectorAll(`input[type="radio"][name="${el.name}"]`)
    const anyChecked = Array.from(group).some(radio => radio.checked)

    group.forEach(radio => radio.setCustomValidity(anyChecked ? "" : "Please select an option"))
  }
}
